import { EDjangoAppsEndpoints } from '../enums/EDjangoAppsEndpoints';
import type { IAugmentedBackendResponse } from '../models/IAugmentedBackendResponse';
import type { ISortingMeta } from '../models/ISortingMeta';
import type { TPandasTable } from '../models/TPandasTable';
import type UserAccountModel from '../models/UserAccountModel';
import {
  type CreateKeycloakUserModel,
  type DeleteSelfResponse,
  type UpdateInfoPayload
} from '../models/UserAccountModel';
import ApiService from './apiService';

export default class UserManagementService extends ApiService {
  /**
   * @deprecated Ticket #589.
   */
  static async createAccount(
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    role: string,
    twoFaRequired: boolean
  ) {
    return await super.post<UserAccountModel>(EDjangoAppsEndpoints.accounts, undefined, {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      role,
      twoFaRequired
    });
  }

  static async getAccountList(page: number, pageSize: number, searchTerm: string) {
    return (
      await super.get<UserAccountModel[]>(EDjangoAppsEndpoints.accounts, undefined, {
        page: page.toString(),
        pageSize: pageSize.toString(),
        searchTerm
      })
    ).getData();
  }

  static async createKeycloakUser(user: CreateKeycloakUserModel) {
    return await super.post<{ userId: string }>(EDjangoAppsEndpoints.userManagement, 'user', user);
  }

  static async getKeycloakUsers(params: { page: number; sorting: ISortingMeta; search?: string }) {
    return await super.get<IAugmentedBackendResponse<TPandasTable>>(EDjangoAppsEndpoints.userManagement, '', {
      page: params.page,
      sortCol: params.sorting.sortCol,
      ascending: params.sorting.ascending,
      searchText: params.search ?? ''
    });
  }

  static async getKeycloakAdminUrl() {
    return await super.get<{ url: string }>(EDjangoAppsEndpoints.userManagement, 'keycloak');
  }

  static async getAccount(email: string) {
    return (await super.get<UserAccountModel>(EDjangoAppsEndpoints.accounts, 'detail', { email })).getData();
  }

  static async updateAccount(
    id: string,
    firstName: string,
    lastName: string,
    email: string,
    role: string,
    twoFaRequired: boolean
  ) {
    return (
      await super.put<UserAccountModel>(EDjangoAppsEndpoints.accounts, undefined, {
        id,
        first_name: firstName,
        last_name: lastName,
        email,
        role,
        twoFaRequired
      })
    ).getData();
  }

  static async deleteAccount(id: string) {
    return (await super.delete(EDjangoAppsEndpoints.userManagement, `delete/${id}`)).getData();
  }

  static async deleteSelf() {
    return await super.delete<DeleteSelfResponse>(EDjangoAppsEndpoints.userManagement, 'delete/self');
  }

  static async updateSelfAccount(payload: Partial<UpdateInfoPayload>) {
    return await super.put<{ success: boolean }>(EDjangoAppsEndpoints.userManagement, 'self-service', payload);
  }

  static async resetPasswordSelf() {
    return await super.get<{ success: boolean }>(EDjangoAppsEndpoints.userManagement, 'user/reset-password');
  }
}
